/**
 * Document Requirements Page - Manage document requirements
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../../components/ConfirmDialog';
import { AdminPageLayout, StatCard, StatGrid } from '../../components';
import {
  Listing,
  ListingActionBar,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  EditAction,
  DeleteAction,
  CreateAction
} from '../../components/listing/v2/ListingAction';
import {
  FileText,
  CheckCircle,
  XCircle,
  Mail,
  Smartphone,
  Clock
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import documentRequirementsService from '../../services/documentRequirementsService';
import apiService from '../../services/api';

const DocumentRequirements = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [complianceStats, setComplianceStats] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    search: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const ASSIGNMENT_TYPES = [
    { value: 'club_wide', label: 'Club-Wide' },
    { value: 'program_specific', label: 'Program-Specific' },
    { value: 'event_specific', label: 'Event-Specific' },
    { value: 'role_specific', label: 'Role-Specific' },
    { value: 'conditional', label: 'Conditional' }
  ];

  const loadRequirements = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentRequirementsService.getClubRequirements(filters.status === 'active');
      setRequirements(data || []);
    } catch (err) {
      console.error('Error loading requirements:', err);
      setError('Failed to load document requirements');
      showError('Error loading document requirements');
    } finally {
      setLoading(false);
    }
  }, [filters.status, showError]);

  const loadComplianceStats = useCallback(async () => {
    try {
      const stats = await documentRequirementsService.getComplianceStats();
      setComplianceStats({
        compliancePercentage: stats.compliance_percentage || 0,
        totalRequirements: stats.total_requirements || 0,
        activeRequirements: stats.active_requirements || 0
      });
    } catch (err) {
      console.error('Error loading compliance stats:', err);
    }
  }, []);

  useEffect(() => {
    loadRequirements();
  }, [loadRequirements]);

  useEffect(() => {
    loadComplianceStats();
  }, [loadComplianceStats]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleDeleteRequirement = async (requirementId, requirementTitle) => {
    setIsDeleting(true);
    try {
      await documentRequirementsService.deleteRequirement(requirementId);
      showSuccess(`Document requirement "${requirementTitle}" deleted successfully.`);
      setShowDeleteModal(null);
      setRequirements(prevRequirements => prevRequirements.filter(r => r.id !== requirementId));
      await loadRequirements();
    } catch (err) {
      console.error('Error deleting requirement:', err);
      showError(`Error deleting document requirement: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredRequirements = requirements.filter(requirement => {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      if (!requirement.title.toLowerCase().includes(searchTerm) &&
        !(requirement.description && requirement.description.toLowerCase().includes(searchTerm))) {
        return false;
      }
    }
    if (filters.type !== 'all') {
      if (requirement.assignment_type !== filters.type) return false;
    }
    return true;
  });

  const getAssignmentTypeLabel = (type) => {
    const assignmentType = ASSIGNMENT_TYPES.find(t => t.value === type);
    return assignmentType ? assignmentType.label : type || 'Unknown';
  };

  const getTypeIcon = (type) => {
    const icons = {
      club_wide: <FileText size={32} />,
      program_specific: <FileText size={32} />,
      event_specific: <FileText size={32} />,
      role_specific: <FileText size={32} />,
      conditional: <FileText size={32} />
    };
    return icons[type] || <FileText size={32} />;
  };

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      filters={[
        {
          name: 'status',
          label: 'Status',
          options: [
            { value: 'all', label: 'All Statuses' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' }
          ]
        },
        {
          name: 'type',
          label: 'Assignment Type',
          options: [
            { value: 'all', label: 'All Types' },
            ...ASSIGNMENT_TYPES
          ]
        }
      ]}
      activeFilters={{ status: filters.status, type: filters.type }}
      onFilterChange={(name, val) => handleFilterChange(name, val)}
      onClearFilters={() => setFilters({ status: 'all', type: 'all', search: '' })}
    />
  );

  const headerActions = (
    <ListingActionBar
      rightSection={
        <CreateAction 
          onClick={() => navigate('/admin/documents/requirements/create')} 
          label="New Requirement" 
        />
      }
    />
  );

  return (
    <AdminPageLayout
      title="DOCUMENT REQUIREMENTS"
      subtitle="Configure automatic document assignments based on roles, programs, and conditions"
      backPath="/admin/documents"
      backLabel="← Back to Documents"
      maxWidth="1400px"
    >
      {/* Statistics */}
      {complianceStats && (
        <StatGrid>
          <StatCard
            number={complianceStats.compliancePercentage}
            label="Member Compliance"
            suffix="%"
          />
          <StatCard
            number={complianceStats.totalRequirements}
            label="Total Requirements"
          />
          <StatCard
            number={complianceStats.activeRequirements}
            label="Active Requirements"
          />
        </StatGrid>
      )}

      {/* Requirements List */}
      <Listing
        loading={loading}
        items={filteredRequirements}
        actions={headerActions}
        filters={filterBar}
        variant="list"
        emptyMessage={error ? `Error: ${error}` : "No document requirements yet. Create one to get started."}
        renderItem={(requirement) => (
          <ListingItem
            key={requirement.id}
            title={requirement.title}
            subtitle={requirement.description}
            icon={getTypeIcon(requirement.assignment_type)}
            actions={
              <div style={{ display: 'flex', gap: '8px' }}>
                <EditAction size="xs" onClick={() => navigate(`/admin/documents/requirements/${requirement.id}/edit`)} />
                <DeleteAction size="xs" onClick={() => {
                  setShowDeleteModal(requirement.id);
                }} />
              </div>
            }
          >
            <div style={{ display: 'flex', gap: '16px', marginTop: '4px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                <FileText size={14} /> {getAssignmentTypeLabel(requirement.assignment_type)}
              </div>
              {requirement.is_mandatory && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', padding: '2px 8px', backgroundColor: 'var(--color-error)', color: 'white', borderRadius: '4px' }}>
                  Mandatory
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem' }}>
                {requirement.active ? (
                  <span style={{ color: 'var(--color-success)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <CheckCircle size={14} /> Active
                  </span>
                ) : (
                  <span style={{ color: 'var(--color-warning)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <XCircle size={14} /> Inactive
                  </span>
                )}
              </div>
              {requirement.due_date_offset_days && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                  <Clock size={14} /> {requirement.due_date_offset_days} days
                </div>
              )}
              {requirement.send_email_notifications && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                  <Mail size={14} /> Email
                </div>
              )}
              {requirement.send_sms_notifications && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                  <Smartphone size={14} /> SMS
                </div>
              )}
            </div>
          </ListingItem>
        )}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (() => {
        const requirement = requirements.find(r => r.id === showDeleteModal);
        return (
          <ConfirmDialog
            isOpen={true}
            title="Delete Document Requirement"
            message={`Are you sure you want to delete "${requirement?.title}"? This action cannot be undone.`}
            variant="danger"
            onConfirm={() => {
              if (requirement) {
                handleDeleteRequirement(showDeleteModal, requirement.title);
              }
            }}
            onClose={() => setShowDeleteModal(null)}
            loading={isDeleting}
            confirmText="Delete"
            cancelText="Cancel"
          />
        );
      })()}
    </AdminPageLayout>
  );
};

export default DocumentRequirements;
